/**
 * Higher-Order Component (HOC) that enhances a wrapped component by providing user enrollment-related functionality.
 * It keeps track of the user's enrollment status, outstanding enrollments, and entitlements.
 * The HOC passes the following props to the wrapped component:
 * - userFullyEnrolled: a boolean indicating if the user is fully enrolled in all courses.
 * - outstandingEnrollmentCount: the number of outstanding enrollments for the user.
 * - hasEnrollment: a boolean indicating if the user has any enrollments.
 * - hasSkus: a boolean indicating if the user has any course SKUs.
 * - outstandingCourses: an array of courses that the user is yet to enroll in or doesn't have entitlements for.
 * The HOC requires the following props to be passed from the parent component:
 * - userLoggedIn: a boolean indicating if the user is logged in.
 * - userEnrollments: an array of user enrollments.
 * - userEntitlements: an array of user entitlements.
 * - courses: an array of all available courses.
 * - applicableSeatTypes: an array of seat types that are applicable to the user.
 * It initializes the state with default values for hasEnrollment, hasSkus, outstandingCourses,
 * userFullyEnrolled, and outstandingEnrollmentCount.
 */

import React, { useCallback, useEffect, useState } from 'react';

/* eslint-disable react/prop-types */
// eslint-disable-next-line func-names
const withUserEnrollment = (WrappedComponent) => function (props) {
  const [hasEnrollment, setHasEnrollment] = useState(false);
  const [hasSkus, setHasSkus] = useState(false);
  const [outstandingCourses, setOutstandingCourses] = useState([]);

  const {
    pageContext: {
      program: { courses, applicableSeatTypes: seatTypes },
    },
    userEntitlements,
    userEnrollments,
    userLoggedIn,
  } = props;

  const isEntitledToCourse = (uuid) => userEntitlements?.some(
    (entitlement) => entitlement.courseUuid === uuid && entitlement.expirationDate === null,
  );

  const isEnrolledInCourseWithCertificate = (courseRunKeys = []) => userEnrollments?.some((enrollment) => {
    // is enrolled in the course
    const isEnrolled = courseRunKeys?.some(
      (key) => enrollment.courseId === key,
    );
      /**
       * Check if any applicableSeatTypes are a substring of an entitlement mode
       * necessary for the no-id-professional case, which should count as an applicableSeatType
       */
    const isApplicableSeatType = seatTypes?.some((seatType) => enrollment.mode.includes(seatType));

    return isEnrolled && isApplicableSeatType;
  });

  const userIsInCourseWithCertificate = (course) => {
    const { courseRunKeys, uuid } = course;

    return (
      isEnrolledInCourseWithCertificate(courseRunKeys)
      || isEntitledToCourse(uuid)
    );
  };

  const getApplicableSeat = (seats = []) => seats?.find((seat) => seatTypes?.includes(seat.type));

  const getApplicableEntitlement = (entitlements = []) => entitlements?.find(
    (entitlement) => (entitlement.expires == null || entitlement.expires > Date.now())
        && seatTypes?.includes(entitlement.mode),
  );

  const getProductForCourse = (course) => {
    const entitlement = getApplicableEntitlement(course.entitlements);
    const { activeCourseRun } = course;
    const seat = activeCourseRun
      ? getApplicableSeat(activeCourseRun.seats)
      : null;
    return entitlement || seat;
  };

  const getOutstandingCourses = () => {
    let hasCourseSku = false;
    let isUserEnrolled = false;

    const remainingCourses = courses
      ?.map(({ course }) => course)
      // reject courses user is enrolled in with a certificate
      ?.filter((courseNode) => {
        const userHasEnrollment = userIsInCourseWithCertificate(courseNode);

        if (userHasEnrollment) {
          isUserEnrolled = true;
        }

        return !userHasEnrollment;
      })
      // reject courses user has entitlements for
      ?.filter((courseNode) => {
        const sku = getProductForCourse(courseNode);
        const skuIsUndefined = sku === undefined;

        if (!skuIsUndefined) {
          hasCourseSku = true;
        }

        return !skuIsUndefined;
      });

    if (
      (hasCourseSku && hasSkus !== hasCourseSku)
      || (hasEnrollment && isUserEnrolled !== hasEnrollment)
    ) {
      setHasSkus(hasCourseSku);
      setHasEnrollment(isUserEnrolled);
    }

    return remainingCourses;
  };

  const memoizedSetOutstandingCourses = useCallback(() => {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const latestOutstandingCourses = userEnrollments?.length + userEntitlements?.length === 0
      ? courses
      : getOutstandingCourses();

    if (outstandingCourses !== latestOutstandingCourses) {
      setOutstandingCourses(latestOutstandingCourses);
    }
  }, [userEnrollments, userEntitlements]);

  useEffect(() => {
    memoizedSetOutstandingCourses();
  }, [userEnrollments, userEntitlements]);

  const outstandingEnrollmentCount = outstandingCourses?.length;
  const userFullyEnrolled = userLoggedIn && outstandingEnrollmentCount === 0;

  return (
    <WrappedComponent
      userFullyEnrolled={userFullyEnrolled}
      outstandingEnrollmentCount={outstandingEnrollmentCount}
      hasEnrollment={hasEnrollment}
      hasSkus={hasSkus}
      outstandingCourses={outstandingCourses}
      {...props}
    />
  );
};

export default withUserEnrollment;
